import { c as _c } from "react/compiler-runtime";
import clsx from "classnames";
import { ContentSummaryTag } from "common/components/ContentSummaryTag";
import { Heading } from "common/components/Heading";
import { ImgixImage, ImgixImageProps } from "common/components/ImgixImage";
import { closestCrop } from "modules/editor/utils/closestCrop";
import { ImageAspectRatio } from "modules/editor/types";
import type { ContentSummaryVariantProps } from "common/components/ContentSummary/types";
import { Image, Maybe } from "common/types/graphql";
interface HeroSummaryImageProps {
  image?: Maybe<Partial<Image>>;
  imageSizes?: string;
  desktopImageAspectRatio: ImageAspectRatio;
}
const GradientOverlay = () => {
  "use memo";

  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div data-testid="gradient-overlay" className="absolute inset-0 bg-linear-to-t from-black from-20% to-60% opacity-85" />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};

/**
 * `HeroSummaryImage` is a functional component that renders an image with a gradient overlay, given the image prop.
 * In case the image prop is undefined, the component will return null.
 * The component uses the `closestCrop` function to determine the best crop for the image based on the provided aspect ratio.
 * Widescreen desktopImageAspectRatio is no longer supported as it was causing double cropping. Since it is not a crop we collect.
 */
const HeroSummaryImage = t0 => {
  "use memo";

  const $ = _c(29);
  const {
    image,
    desktopImageAspectRatio,
    imageSizes
  } = t0;
  if (image?.src === undefined || desktopImageAspectRatio === ImageAspectRatio.Widescreen) {
    return null;
  }
  let t1;
  if ($[0] !== image || $[1] !== imageSizes) {
    t1 = {
      ...image,
      src: image.src,
      sizes: imageSizes
    };
    $[0] = image;
    $[1] = imageSizes;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const imageProps = t1;
  let t2;
  if ($[3] !== image.crops) {
    t2 = image.crops || [];
    $[3] = image.crops;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] !== t2) {
    t3 = closestCrop(ImageAspectRatio.Square, t2);
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const squareCrop = t3;
  let t4;
  if ($[7] !== image.crops) {
    t4 = image.crops || [];
    $[7] = image.crops;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  if ($[9] !== desktopImageAspectRatio || $[10] !== t4) {
    t5 = closestCrop(desktopImageAspectRatio, t4);
    $[9] = desktopImageAspectRatio;
    $[10] = t4;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const crop = t5;
  const t6 = desktopImageAspectRatio === ImageAspectRatio.Landscape;
  const t7 = desktopImageAspectRatio === ImageAspectRatio.Ultrawide;
  const t8 = desktopImageAspectRatio === ImageAspectRatio.Pano;
  let t9;
  if ($[12] !== t6 || $[13] !== t7 || $[14] !== t8) {
    t9 = clsx("absolute inset-0", {
      "sm:aspect-3/2": t6,
      "sm:aspect-3/1": t7,
      "sm:aspect-2/1": t8
    });
    $[12] = t6;
    $[13] = t7;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== crop || $[17] !== image.alt || $[18] !== imageProps) {
    t10 = <div className="absolute inset-0 hidden sm:block"><ImgixImage {...imageProps} crop={crop} alt={image.alt} /></div>;
    $[16] = crop;
    $[17] = image.alt;
    $[18] = imageProps;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== image.alt || $[21] !== imageProps || $[22] !== squareCrop) {
    t11 = <div className="absolute inset-0 sm:hidden"><ImgixImage {...imageProps} crop={squareCrop} alt={image.alt} /></div>;
    $[20] = image.alt;
    $[21] = imageProps;
    $[22] = squareCrop;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = <GradientOverlay />;
    $[24] = t12;
  } else {
    t12 = $[24];
  }
  let t13;
  if ($[25] !== t10 || $[26] !== t11 || $[27] !== t9) {
    t13 = <><div className={t9}>{t10}{t11}{t12}</div></>;
    $[25] = t10;
    $[26] = t11;
    $[27] = t9;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  return t13;
};

/**
 * The `Hero` component is a variant of the `ContentSummary` component.
 * The summary image will use the crop based on the provided `desktopImageAspectRatio` prop
 * or square for small screens
 */
export const Hero = t0 => {
  "use memo";

  const $ = _c(29);
  const {
    title,
    url,
    byline,
    hideByline,
    contentType,
    imageSizes,
    image,
    desktopImageAspectRatio,
    tagLabel,
    tagStyle,
    anchorTarget,
    className
  } = t0;
  const t1 = !desktopImageAspectRatio || desktopImageAspectRatio === ImageAspectRatio.Square;
  const t2 = desktopImageAspectRatio === ImageAspectRatio.Landscape;
  const t3 = desktopImageAspectRatio === ImageAspectRatio.Ultrawide;
  const t4 = desktopImageAspectRatio === ImageAspectRatio.Pano;
  let t5;
  if ($[0] !== className || $[1] !== t1 || $[2] !== t2 || $[3] !== t3 || $[4] !== t4) {
    t5 = clsx("group relative flex min-h-96 w-full flex-col justify-end outline-offset-4", className, {
      "sm:min-h-80 lg:min-h-96 xl:min-h-120": t1,
      "sm:aspect-3/2 sm:min-h-0": t2,
      "sm:aspect-3/1 sm:min-h-0": t3,
      "sm:aspect-2/1 sm:min-h-0": t4
    });
    $[0] = className;
    $[1] = t1;
    $[2] = t2;
    $[3] = t3;
    $[4] = t4;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] !== desktopImageAspectRatio || $[7] !== image || $[8] !== imageSizes) {
    t6 = desktopImageAspectRatio && <HeroSummaryImage image={image} imageSizes={imageSizes} desktopImageAspectRatio={desktopImageAspectRatio} />;
    $[6] = desktopImageAspectRatio;
    $[7] = image;
    $[8] = imageSizes;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== contentType || $[11] !== tagLabel || $[12] !== tagStyle) {
    t7 = <ContentSummaryTag contentType={contentType} tagLabel={tagLabel} tagStyle={tagStyle} />;
    $[10] = contentType;
    $[11] = tagLabel;
    $[12] = tagStyle;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const t8 = title || "";
  let t9;
  if ($[14] !== t8) {
    t9 = <span className="drop-shadow-dark group-hover:underline"><Heading level={3}><span dangerouslySetInnerHTML={{
          __html: t8
        }} /></Heading></span>;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== byline || $[17] !== hideByline) {
    t10 = byline && !hideByline && <p className="line-clamp-3 text-sm font-bold drop-shadow">{byline}</p>;
    $[16] = byline;
    $[17] = hideByline;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7 || $[21] !== t9) {
    t11 = <section className="relative flex max-w-(--breakpoint-md) flex-col items-start gap-4 p-4 text-white md:p-8 xl:max-w-(--breakpoint-lg)">{t7}{t9}{t10}</section>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== anchorTarget || $[24] !== t11 || $[25] !== t5 || $[26] !== t6 || $[27] !== url) {
    t12 = <article className="contents"><a href={url} target={anchorTarget} className={t5}>{t6}{t11}</a></article>;
    $[23] = anchorTarget;
    $[24] = t11;
    $[25] = t5;
    $[26] = t6;
    $[27] = url;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};